// RegionCard.tsx — the Region block inside Settings → Language & Region.
//
// Two separate things, and keeping them separate is the whole design:
//
//   ACCOUNT REGION      where the account was opened. Set once, at sign-up, from
//                       the country code the network edge supplies. Read-only
//                       forever — there is no write path on the server, and a
//                       test asserts a PATCH attempting one is ignored.
//   REGIONAL INTERESTS  places you want to see more from. Changeable, additive,
//                       and viewer-side only: they REORDER suggestions, they
//                       never hide anyone. That keeps the neighbouring Content
//                       Preferences invariant intact (layout, never content).
//
// Visibility is off by default and opt-in. While it is off, no other member can
// see either value. The switch says plainly what turning it on does, because a
// platform-rendered locality claim is the standard opener for "I'm local to you"
// fraud, and for members whose speech is unwelcome where they live it is a way
// to be found by nationality.
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Panel, Switch, useToast } from '@/ui';
import { useSession } from '@/hooks';
import { regionsService, type RegionOption } from '@/core/services/regionsService';

const Card = styled(Panel)`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.md};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const RowText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const RowLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const RowHint = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  max-width: 44ch;
`;

const ReadOnlyValue = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Chip = styled.button<{ $on: boolean }>`
  padding: 5px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  cursor: pointer;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  border: 1px solid ${({ $on, theme }) => ($on ? 'transparent' : theme.color.glassBorder)};
  background: ${({ $on, theme }) =>
    $on ? `color-mix(in srgb, ${theme.color.accentCyan} 14%, transparent)` : 'transparent'};
  color: ${({ $on, theme }) => ($on ? theme.color.textPrimary : theme.color.textMuted)};
  &:hover {
    color: ${({ theme }) => theme.color.textPrimary};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.accentCyan};
    outline-offset: 2px;
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const Select = styled.select`
  padding: 6px 10px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme }) => theme.color.surface};
  color: ${({ theme }) => theme.color.textPrimary};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  max-width: 260px;
`;

const Caution = styled.p`
  margin: -6px 0 0;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.safety};
  max-width: 52ch;
`;

export function RegionCard() {
  const { currentUser, updateProfile } = useSession();
  const toast = useToast();
  const [catalogue, setCatalogue] = useState<RegionOption[]>([]);
  const [maxInterests, setMaxInterests] = useState(5);
  // Local echo of the server value so a chip responds on click rather than
  // after the round-trip. Keyed on the server list: when the PATCH resolves and
  // currentUser updates, React remounts this state with the authoritative value
  // instead of us re-syncing it from an effect.
  const serverInterests = currentUser?.regionInterests ?? [];
  const [picked, setPicked] = useState<string[]>(serverInterests);
  const [syncedTo, setSyncedTo] = useState(serverInterests.join(','));
  if (syncedTo !== serverInterests.join(',')) {
    setSyncedTo(serverInterests.join(','));
    setPicked(serverInterests);
  }

  useEffect(() => {
    let live = true;
    void regionsService.list().then((c) => {
      if (!live) return;
      setCatalogue(c.regions);
      setMaxInterests(c.maxInterests);
    });
    return () => {
      live = false;
    };
  }, []);

  if (!currentUser) return null;

  const baseRegion = currentUser.baseRegion ?? '';
  const baseName = catalogue.find((r) => r.code === baseRegion)?.name ?? '';
  const isPublic = currentUser.regionPublic === true;

  async function commit(next: string[]) {
    setPicked(next);
    await updateProfile({ regionInterests: next });
  }

  function toggle(code: string) {
    if (picked.includes(code)) {
      void commit(picked.filter((c) => c !== code));
    } else if (picked.length < maxInterests) {
      void commit([...picked, code]);
    }
  }

  async function togglePublic(on: boolean) {
    await updateProfile({ regionPublic: on });
    toast(
      on
        ? 'Your account region is now shown on your profile'
        : 'Your region is private again',
    );
  }

  return (
    <Card>
      <CardTitle>Region</CardTitle>

      {/* Account region — read-only, always */}
      <Row>
        <RowText>
          <RowLabel>Account region</RowLabel>
          <RowHint>
            {baseRegion
              ? 'Where this account was opened. This never changes, even if you move. It is not a setting.'
              : 'We did not record a region when this account was created. That will not change.'}
          </RowHint>
        </RowText>
        <ReadOnlyValue>{baseName || baseRegion || 'Not recorded'}</ReadOnlyValue>
      </Row>

      {/* Regional interests — the changeable half */}
      <Row>
        <RowText>
          <RowLabel>Regional interests</RowLabel>
          <RowHint>
            Places you want to see more from. Picking some moves matching accounts up your
            suggestions. It never hides anyone, and it does not change where you are.
            Pick up to {maxInterests}, or none for everywhere.
          </RowHint>
        </RowText>
        <Select
          aria-label="Add a regional interest"
          value=""
          disabled={picked.length >= maxInterests}
          onChange={(e) => {
            if (e.target.value) toggle(e.target.value);
          }}
        >
          <option value="">
            {picked.length >= maxInterests ? `Limit of ${maxInterests} reached` : 'Add a region…'}
          </option>
          {catalogue
            .filter((r) => !picked.includes(r.code))
            .map((r) => (
              <option key={r.code} value={r.code}>
                {r.name}
              </option>
            ))}
        </Select>
      </Row>

      {picked.length > 0 && (
        <Chips role="group" aria-label="Selected regional interests">
          {picked.map((code) => (
            <Chip
              key={code}
              $on
              type="button"
              aria-label={`Remove ${catalogue.find((r) => r.code === code)?.name ?? code}`}
              onClick={() => toggle(code)}
            >
              {catalogue.find((r) => r.code === code)?.name ?? code} ✕
            </Chip>
          ))}
        </Chips>
      )}

      {/* Visibility — off by default */}
      <Row>
        <RowText>
          <RowLabel>Show my region on my profile</RowLabel>
          <RowHint>
            Off by default. While it is off, nobody else can see your account region or your
            regional interests.
          </RowHint>
        </RowText>
        <Switch
          checked={isPublic}
          onChange={(v) => void togglePublic(v)}
          label="Show my region on my profile"
        />
      </Row>
      {isPublic && (
        <Caution>
          Your account region is visible to anyone who opens your profile. People who want to
          seem local to you are a common way scams begin, and in some places being identifiable
          by country carries a real risk. Your regional interests stay private either way.
        </Caution>
      )}
      {!baseRegion && isPublic && (
        <RowHint>
          Nothing is shown at the moment, because no region was recorded for this account.
        </RowHint>
      )}
    </Card>
  );
}
